import React, { useState, useEffect } from 'react';
import { useKanbanStore } from './store/useKanbanStore';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserManagementModal: React.FC<UserManagementModalProps> = ({ isOpen, onClose }) => {
  // Destructure resetPassword along with other store actions/state
  const { users = [], fetchUsers, createUser, updateUserAdminStatus, resetPassword, currentUser, setCurrentUser } = useKanbanStore() as any;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');

  // Track password reset state per user
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchUsers?.();
      setResettingUserId(null);
      setNewPasswordInput('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await createUser(username, password, isAdmin);
      setUsername('');
      setPassword('');
      setIsAdmin(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 sm:backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white dark:bg-slate-900 rounded-none sm:rounded-xl shadow-2xl w-full h-full sm:w-auto sm:max-w-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col sm:max-h-[90vh]">
        {/* Header */}
        <div className="px-3 py-4 sm:px-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">User Management</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm font-semibold"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-3 sm:p-6 overflow-y-auto space-y-6 flex-1">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-950/50 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Create User Form */}
          <form onSubmit={handleCreateUser} className="space-y-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
            <h3 className="font-semibold text-sm text-slate-700 dark:text-slate-300">Add New User</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                />
                <span>Administrator Privileges</span>
              </label>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-semibold transition-colors shadow-sm"
              >
                Create User
              </button>
            </div>
          </form>

          {/* User List */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-slate-700 dark:text-slate-300">Existing Users</h3>
            <div className="divide-y divide-slate-200 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
              {Array.isArray(users) && users.length > 0 ? (
                users.map((user: any) => (
                  <div key={user.id} className="p-4 flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-medium text-slate-800 dark:text-slate-100 text-sm flex items-center gap-2">
                          {user.username}
                          {currentUser?.id === user.id && (
                            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300 text-[10px] font-bold rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Role: {user.isAdmin ? 'Admin' : 'Standard User'}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateUserAdminStatus?.(user.id, !user.isAdmin)}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-xs font-medium transition-colors"
                        >
                          {user.isAdmin ? 'Revoke Admin' : 'Make Admin'}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setResettingUserId(resettingUserId === user.id ? null : user.id);
                            setNewPasswordInput('');
                          }}
                          className="px-3 py-1 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/50 dark:hover:bg-amber-900/50 text-amber-600 dark:text-amber-400 rounded text-xs font-medium transition-colors"
                        >
                          {resettingUserId === user.id ? 'Cancel' : 'Reset Pwd'}
                        </button>

                        {currentUser?.id !== user.id && (
                          <button
                            type="button"
                            onClick={() => setCurrentUser?.(user)}
                            className="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded text-xs font-medium transition-colors"
                          >
                            Switch To
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Inline Password Reset Drawer */}
                    {resettingUserId === user.id && (
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <input
                          type="password"
                          placeholder="New temporary password"
                          value={newPasswordInput}
                          onChange={(e) => setNewPasswordInput(e.target.value)}
                          className="flex-1 px-3 py-1.5 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            if (!newPasswordInput) return alert('Please enter a new password');
                            await resetPassword(user.id, newPasswordInput);
                            setResettingUserId(null);
                            setNewPasswordInput('');
                          }}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold transition-colors"
                        >
                          Save Password
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-slate-500 dark:text-slate-400 text-sm">
                  No users found. Create one above!
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 py-4 sm:px-6 border-t border-slate-200 dark:border-slate-800 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-md text-sm font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};